/**
 * How the connected accounts can be ordered.
 *
 * Every field here already comes back from `/social_stats/summary` — none of
 * this asks the server for anything new, and with a handful of accounts
 * sorting them in the browser is free.
 *
 * Two different questions about growth, deliberately kept apart:
 *
 *   `followers_pct`   which account is growing FASTEST — a small account
 *                     adding 200 is doing better than a large one adding 400
 *   `followers_delta` which account added the MOST people — the one that
 *                     actually moved the audience number
 *
 * Collapsing them into one "growth" sort would answer neither reliably: sorted
 * by percentage a 40-follower account tops the list forever, and sorted by
 * absolute the biggest account wins whatever it does.
 *
 * Nulls sort LAST in every case, matching the backend's own convention
 * (`social_stats/routes.py`): an account with no score yet is unknown, not
 * bad, and burying it under the worst performers says the wrong thing.
 */
export const SORTS = [
  { value: 'followers', label: 'Most followers', field: 'followers', dir: 'desc' },
  { value: 'growth_pct', label: 'Fastest growing', field: 'followers_pct', dir: 'desc' },
  { value: 'growth_abs', label: 'Followers gained', field: 'followers_delta', dir: 'desc' },
  { value: 'engagement', label: 'Best engagement', field: 'net_engagement_rate', dir: 'desc' },
  { value: 'score', label: 'Highest score', field: 'score', dir: 'desc' },
  { value: 'breaking_out', label: 'Most breaking out', field: 'breaking_out_count', dir: 'desc' },
  { value: 'reach', label: 'Most views per post', field: 'avg_views_per_post', dir: 'desc' },
  { value: 'name', label: 'Name', field: 'username', dir: 'asc' },
];

export const SORT_VALUES = SORTS.map((s) => s.value);

export const sortAccounts = (accounts, value) => {
  const sort = SORTS.find((s) => s.value === value) || SORTS[0];
  const { field, dir } = sort;

  // A copy: the summary object is state, and sorting in place mutates what
  // React is holding, which shows up later as a list that will not re-render.
  return [...(accounts || [])].sort((a, b) => {
    const x = a?.[field];
    const y = b?.[field];

    // Unknown last, whichever direction the rest is going.
    const xMissing = x === null || x === undefined || x === '';
    const yMissing = y === null || y === undefined || y === '';
    if (xMissing && yMissing) return 0;
    if (xMissing) return 1;
    if (yMissing) return -1;

    if (typeof x === 'string' || typeof y === 'string') {
      const cmp = String(x).localeCompare(String(y), undefined, { sensitivity: 'base' });
      return dir === 'asc' ? cmp : -cmp;
    }
    return dir === 'asc' ? x - y : y - x;
  });
};
